import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { DetectedBrandTermSkeleton, DetectedBrandTerm } from 'src/components/DetectedBrandTerm';
import { BrandVoiceSettingsTitle } from 'src/components/BrandVoiceSettingsTitle';
import { InsightBox } from '../InsightBox';
import { useStyles } from './useStyles';

const propTypes = {
  classes: PropTypes.shape({}),
  terms: PropTypes.arrayOf(
    PropTypes.shape({
      brandTerm: PropTypes.shape({}).isRequired,
      termInText: PropTypes.string.isRequired,
    })
  ).isRequired,
  onApplyDetectedBrandTerm: PropTypes.func.isRequired,
  onEditBrandTerm: PropTypes.func,
  onExpand: PropTypes.func,
  onBrandVoiceSettingsClick: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  disabled: PropTypes.bool,
  hasBrandTerms: PropTypes.bool,
  insightsBoxBorder: PropTypes.bool,
  shrunk: PropTypes.bool,
  brandRulesBrandVoice: PropTypes.shape({
    name: PropTypes.string,
  }),
  showBrandVoicesSettings: PropTypes.bool,
};

const defaultProps = {
  classes: null,
  onEditBrandTerm: null,
  onExpand: null,
  loading: false,
  disabled: false,
  hasBrandTerms: false,
  insightsBoxBorder: false,
  shrunk: false,
  brandRulesBrandVoice: null,
  showBrandVoicesSettings: false,
};

export const BrandTerms = ({
  classes,
  terms,
  onApplyDetectedBrandTerm,
  onEditBrandTerm,
  onExpand,
  onBrandVoiceSettingsClick,
  loading,
  disabled,
  hasBrandTerms,
  insightsBoxBorder,
  shrunk,
  brandRulesBrandVoice,
  showBrandVoicesSettings,
}) => {
  const styles = useStyles({ classes });

  const subtitle = useMemo(() => {
    if (loading || terms?.length === 1) {
      return '';
    }

    if (!hasBrandTerms) {
      return 'There are no brand rules set for your business';
    }

    if (terms?.length === 0) {
      return 'No rules were found';
    }

    return `${terms.length} rules were found`;
  }, [hasBrandTerms, loading, terms.length]);

  return (
    <div className={styles.root}>
      <BrandVoiceSettingsTitle
        title="Voice"
        showSettings={showBrandVoicesSettings}
        settingsTooltipText={`Selected brand voice: ${brandRulesBrandVoice?.name}`}
        onSettingsClick={onBrandVoiceSettingsClick}
      />
      <InsightBox
        className={insightsBoxBorder ? styles.border : null}
        classes={{ collapseWrapperInner: styles.collapseWrapperInner }}
        title="Brand Rules"
        subtitle={subtitle}
        onExpand={onExpand}
        collapsableContent={terms?.slice(1).map(({ brandTerm, termInText }) => (
          <DetectedBrandTerm
            className={styles.detectedBrandTerm}
            key={brandTerm.id}
            brandTerm={brandTerm}
            termInText={termInText}
            onApplyClick={onApplyDetectedBrandTerm}
            onEditClick={onEditBrandTerm}
            disabled={disabled}
            shrunk={shrunk}
          />
        ))}
      >
        {loading ? (
          <DetectedBrandTermSkeleton />
        ) : (
          <>
            {!!terms[0] && (
              <DetectedBrandTerm
                className={styles.detectedBrandTerm}
                brandTerm={terms[0].brandTerm}
                termInText={terms[0].termInText}
                onApplyClick={onApplyDetectedBrandTerm}
                onEditClick={onEditBrandTerm}
                disabled={disabled}
                shrunk={shrunk}
              />
            )}
          </>
        )}
      </InsightBox>
    </div>
  );
};

BrandTerms.propTypes = propTypes;
BrandTerms.defaultProps = defaultProps;
